import useSWR from "swr";
import { useServicesContext } from "../../providers/ServicesProvider";
import { DeveloperSettings } from "../../domain/DeveloperSettings";
import {
    useSuperAdminOrganizations,
    SuperAdminOrganization
} from "../useSuperAdminOrganizations/useSuperAdminOrganizations";

export type DeveloperSettingsByOrg = Record<string, DeveloperSettings>;

const matchesSearch = (
    org: SuperAdminOrganization,
    developer: DeveloperSettings | undefined,
    search: string
): boolean => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const name = org.organizationName?.toLowerCase() ?? "";
    const vendor = developer?.vendorName?.toLowerCase() ?? "";
    return name.includes(term) || vendor.includes(term);
};

/**
 * Backs the Support > Developer Settings page: returns every organization the user is a super admin
 * of together with each org's developer settings (enabled + vendor name), filtered by a search term
 * that matches on organization name OR vendor name, and paginated.
 *
 * Server-side-ready seam: `search`/`page`/`pageSize` are accepted as inputs here, but filtering and
 * paging are currently done client-side over the full accessible list. Vendor-name matching needs
 * vendor data for ALL accessible orgs, so settings are loaded up front via
 * `getDeveloperSettingsForOrgs` (a temporary fan-out; see that method's TODO). When a bulk/searchable
 * endpoint lands, pass `search`/`page`/`pageSize` straight through to the service and drop the
 * client-side filter/slice — the page's contract here stays the same.
 *
 * `enabled` (default true) gates the fan-out: pass the developer-settings feature flag so that no
 * settings requests fire when the feature is off (the SWR key becomes null).
 */
export const useDeveloperSettingsSearch = ({
    search,
    page,
    pageSize,
    enabled = true
}: {
    search: string;
    page: number;
    pageSize: number;
    enabled?: boolean;
}) => {
    const { organizationSettingsService } = useServicesContext();
    const { organizations, isLoading: orgsLoading, error: orgsError } = useSuperAdminOrganizations();

    const organizationIds = organizations.map((org) => org.organizationId);
    const sortedIds = [...organizationIds].sort();
    const key = enabled && sortedIds.length ? `developer-settings-search:${sortedIds.join(",")}` : null;

    const {
        data: developerSettingsByOrg,
        error: settingsError,
        mutate
    } = useSWR<DeveloperSettingsByOrg, Error>(key, () =>
        organizationSettingsService!.getDeveloperSettingsForOrgs({ organizationIds: sortedIds })
    );

    const error = settingsError ?? orgsError;
    const settings = developerSettingsByOrg || {};

    const filtered = organizations.filter((org) => matchesSearch(org, settings[org.organizationId], search));

    const start = (page - 1) * pageSize;
    const currentPageOrganizations = filtered.slice(start, start + pageSize);

    const updateDeveloperSettings = async ({
        organizationId,
        enabled: developerEnabled,
        vendorName
    }: {
        organizationId: string;
        enabled: boolean;
        vendorName: string;
    }): Promise<DeveloperSettings | undefined> => {
        // `!` for consistency with the SWR fetcher above: a missing service is a programming error
        // (no ServicesProvider), not a handled case — fail loudly rather than silently mutate the cache.
        const result = await organizationSettingsService!.updateDeveloperSettings({
            organizationId,
            settings: { enabled: developerEnabled, vendorName }
        });

        // Reflect the submitted values, not the response body: the PUT may be a no-content (204), in
        // which case the adapted result carries defaults rather than what we saved. The functional
        // updater merges into the latest cache value so concurrent row saves can't clobber each other.
        mutate(
            (current) => ({
                ...(current || {}),
                [organizationId]: new DeveloperSettings({ enabled: developerEnabled, vendorName })
            }),
            { revalidate: false }
        );

        return result;
    };

    return {
        organizations: currentPageOrganizations,
        developerSettingsByOrg: settings,
        totalMatched: filtered.length,
        totalOrganizations: organizations.length,
        loading: enabled && (orgsLoading || (!!key && !error && !developerSettingsByOrg)),
        error,
        updateDeveloperSettings
    };
};
